import { useRef, useEffect, useCallback, useState } from "react";
import { Select } from "@react-three/drei";
import { usePlacedModelsStore, useModelStore, useUserInterfaceStore } from "../../store";
import PlexianAvatar from "../Avatar/PlexianAvatar";
import { avatarApi } from "../../services/avatarApi";

// Must match GLBObject.js / CustomTransformControls so scale round-trips correctly
const ASSET_SCALE_FACTOR = 0.85;

// In-memory cache for fetched avatar configs (persists for session)
const avatarConfigCache = new Map();

/**
 * Hook to resolve an avatar's config — either from the model directly
 * (local placement) or by fetching from the backend via avatarId.
 */
function useAvatarConfig(model) {
  const [config, setConfig] = useState(model.avatarConfig || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    // If config is already on the model (local placement), use it
    if (model.avatarConfig) {
      setConfig(model.avatarConfig);
      setLoading(false);
      setError(false);
      return;
    }

    // If we only have an avatarId, fetch the config
    if (model.avatarId) {
      // Check cache first
      if (avatarConfigCache.has(model.avatarId)) {
        setConfig(avatarConfigCache.get(model.avatarId));
        setLoading(false);
        setError(false);
        return;
      }

      setLoading(true);
      setError(false);

      avatarApi.getAvatarById(model.avatarId).then((result) => {
        if (result && result.config) {
          avatarConfigCache.set(model.avatarId, result.config);
          setConfig(result.config);
        } else {
          setError(true);
        }
        setLoading(false);
      }).catch(() => {
        setError(true);
        setLoading(false);
      });
    }
  }, [model.avatarConfig, model.avatarId]);

  return { config, loading, error };
}

function PlacedAvatarObject({ model }) {
  const groupRef = useRef();
  const { config, loading, error } = useAvatarConfig(model);
  // CenterRaycaster dispatches `plexian-avatar-click` with the clicked modelId
  // when the player clicks a placed avatar. Bumping the trigger tells the
  // child PlexianAvatar to flip into interactive mode.
  const [interactionTrigger, setInteractionTrigger] = useState(0);
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.modelId === model.id) {
        setInteractionTrigger((n) => n + 1);
      }
    };
    window.addEventListener('plexian-avatar-click', handler);
    return () => window.removeEventListener('plexian-avatar-click', handler);
  }, [model.id]);

  const visualScale = model.scale.map((s) => s * ASSET_SCALE_FACTOR);

  useEffect(() => {
    if (!groupRef.current) return;

    // Tag this group so the existing selection + transform systems recognise it
    groupRef.current.userData.isGroupRoot = true;
    groupRef.current.userData.sourceId = model.id;

    // Auto-select when freshly placed (matches GLBObject pending-selection pattern)
    const { pendingSelectionId, setPendingSelectionId, setSelectedMeshes, setSelectedUUIDs } =
      useModelStore.getState();

    if (pendingSelectionId === model.id) {
      setSelectedMeshes([groupRef.current]);
      setSelectedUUIDs(new Set([groupRef.current.uuid]));
      setPendingSelectionId(null);
    }
  });

  // Placeholder mesh for loading/error states
  if (loading || error || !config) {
    return (
      <group
        ref={groupRef}
        position={model.position}
        rotation={model.rotation}
        scale={visualScale}
        userData={{ isGroupRoot: true, sourceId: model.id }}
      >
        <mesh>
          <capsuleGeometry args={[0.3, 0.8, 4, 8]} />
          <meshStandardMaterial
            color={error ? "#ff4444" : "#888888"}
            transparent
            opacity={loading ? 0.4 : 0.6}
          />
        </mesh>
      </group>
    );
  }

  return (
    <group
      ref={groupRef}
      position={model.position}
      rotation={model.rotation}
      scale={visualScale}
      userData={{ isGroupRoot: true, sourceId: model.id }}
    >
      <PlexianAvatar config={config} interactionTrigger={interactionTrigger} />
    </group>
  );
}

/**
 * PlacedAvatars — renders all placed models with type "plexian_avatar".
 * Plugs into the existing Select → CustomTransformControls pipeline
 * by tagging each group with userData.isGroupRoot + userData.sourceId.
 */
export default function PlacedAvatars() {
  const placedModels = usePlacedModelsStore((s) => s.placedModels);
  const avatarModels = placedModels.filter((m) => m.type === "plexian_avatar");
  const playerMode = useUserInterfaceStore((s) => s.playerMode);
  const isEditing = playerMode !== "player";

  const setSelectedUUIDs = useModelStore((s) => s.setSelectedUUIDs);
  const setSelectedMeshes = useModelStore((s) => s.setSelectedMeshes);
  const isTransforming = useUserInterfaceStore((s) => s.isTransforming);

  const handleSelect = useCallback(
    (ev) => {
      if (isTransforming) return;

      const selectedGroups = new Set();
      const selectedMeshes = [];

      for (const mesh of ev) {
        let parent = mesh;
        while (parent && !parent.userData?.isGroupRoot) {
          parent = parent.parent;
        }
        if (parent?.userData?.isGroupRoot) {
          selectedGroups.add(parent);
          selectedMeshes.push(parent);
        }
      }

      const selected = Array.from(selectedGroups);
      setSelectedMeshes(selected);
      setSelectedUUIDs(selected.map((o) => o.uuid));
    },
    [setSelectedMeshes, setSelectedUUIDs, isTransforming]
  );

  if (avatarModels.length === 0) return null;

  const avatarMeshes = avatarModels.map((model) => (
    <PlacedAvatarObject key={model.id} model={model} />
  ));

  // In player mode skip the Select wrapper — no editing needed
  if (!isEditing) return <>{avatarMeshes}</>;

  return (
    <Select multiple box onChangePointerUp={handleSelect}>
      {avatarMeshes}
    </Select>
  );
}
